import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Modal, Field, inputCls, PrimaryBtn, GhostBtn, DangerBtn, DataTable } from "../components/DataTable";
import SiteSelector, { useSiteSelector } from "../components/SiteSelector";
import { toast } from "sonner";

const empty = {
  device_id: "", site_id: "", name: "", unit: "", tag_type: "analog",
  min_value: 0, max_value: 100, sim_pattern: "sine",
  warning_low: 20, warning_high: 80, critical_low: 10, critical_high: 90,
  param_name: "", scale: 1, offset: 0,
  register_address: "", register_type: "holding", register_count: 1, opcua_node_id: "",
};

export default function Tags() {
  const { sites, siteId, setSiteId } = useSiteSelector();
  const [rows, setRows] = useState([]);
  const [devices, setDevices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => siteId && api.get("/tags", { params: { site_id: siteId } }).then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    if (siteId) api.get("/devices", { params: { site_id: siteId } }).then(({ data }) => setDevices(data));
  }, [siteId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        min_value: parseFloat(form.min_value), max_value: parseFloat(form.max_value),
        warning_low: form.warning_low === "" ? null : parseFloat(form.warning_low),
        warning_high: form.warning_high === "" ? null : parseFloat(form.warning_high),
        critical_low: form.critical_low === "" ? null : parseFloat(form.critical_low),
        critical_high: form.critical_high === "" ? null : parseFloat(form.critical_high),
        scale: parseFloat(form.scale) || 1,
        offset: parseFloat(form.offset) || 0,
        register_address: form.register_address === "" || form.register_address === null ? null : parseInt(form.register_address),
        register_count: parseInt(form.register_count) || 1,
      };
      if (editId) await api.put(`/tags/${editId}`, payload);
      else await api.post("/tags", payload);
      toast.success("Saved"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete tag?")) return;
    await api.delete(`/tags/${id}`); load(); toast.success("Deleted");
  };

  const deviceName = (id) => devices.find(d => d.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">SENSOR CONFIGURATION</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Tags</h1>
        </div>
        <div className="flex items-center gap-3">
          <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />
          <PrimaryBtn data-testid="add-tag-btn" onClick={() => { setForm({ ...empty, device_id: devices[0]?.id || "", site_id: siteId || devices[0]?.site_id || "" }); setEditId(null); setOpen(true); }}>+ Add Tag</PrimaryBtn>
        </div>
      </div>

      <DataTable
        testId="tags-table"
        columns={[
          { key: "name", label: "NAME", w: "1.6fr" },
          { key: "param_name", label: "PARAM", w: "0.5fr", render: r => <span className="font-mono-metric text-[#00E5FF]">{r.param_name || "—"}</span> },
          { key: "device_id", label: "DEVICE", render: r => deviceName(r.device_id) },
          { key: "tag_type", label: "TYPE", w: "0.6fr" },
          { key: "unit", label: "UNIT", w: "0.5fr" },
          { key: "current_value", label: "VALUE", w: "0.7fr", render: r => <span className="font-mono-metric">{r.current_value?.toFixed?.(2) ?? "—"}</span> },
          { key: "current_status", label: "STATUS", w: "0.7fr", render: r => (
            <span className="font-mono-metric uppercase text-[10px]" style={{ color: r.current_status === "critical" ? "#FF3B30" : r.current_status === "warning" ? "#FFB300" : "#00E676" }}>{r.current_status}</span>
          ) },
        ]}
        rows={rows}
        actions={(r) => (
          <>
            <GhostBtn data-testid={`edit-tag-${r.id}`} onClick={() => { setForm(r); setEditId(r.id); setOpen(true); }}>Edit</GhostBtn>
            <DangerBtn data-testid={`del-tag-${r.id}`} onClick={() => remove(r.id)}>Del</DangerBtn>
          </>
        )}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Tag" : "Add Tag"} testId="tag-modal">
        <form onSubmit={save} className="max-h-[70vh] overflow-y-auto pr-2">
          <Field label="Device">
            <select required value={form.device_id} onChange={(e) => {
              const d = devices.find(x => x.id === e.target.value);
              setForm({ ...form, device_id: e.target.value, site_id: d?.site_id || "" });
            }} className={inputCls()} data-testid="tag-device">
              <option value="">— select —</option>
              {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls()} data-testid="tag-name" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Gateway Param (e.g. P1)"><input value={form.param_name || ""} onChange={(e) => setForm({ ...form, param_name: e.target.value })} className={inputCls()} data-testid="tag-param" /></Field>
            <Field label="Scale"><input type="number" step="any" value={form.scale} onChange={(e) => setForm({ ...form, scale: e.target.value })} className={inputCls()} /></Field>
            <Field label="Offset"><input type="number" step="any" value={form.offset} onChange={(e) => setForm({ ...form, offset: e.target.value })} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><select value={form.tag_type} onChange={(e) => setForm({ ...form, tag_type: e.target.value })} className={inputCls()}><option>analog</option><option>digital</option></select></Field>
            <Field label="Unit"><input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min"><input type="number" step="any" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} className={inputCls()} /></Field>
            <Field label="Max"><input type="number" step="any" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value })} className={inputCls()} /></Field>
          </div>
          <Field label="Simulation Pattern">
            <select value={form.sim_pattern} onChange={(e) => setForm({ ...form, sim_pattern: e.target.value })} className={inputCls()}>
              <option>sine</option><option>walk</option><option>random</option><option>bool</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Warn Low"><input type="number" step="any" value={form.warning_low ?? ""} onChange={(e) => setForm({ ...form, warning_low: e.target.value })} className={inputCls()} /></Field>
            <Field label="Warn High"><input type="number" step="any" value={form.warning_high ?? ""} onChange={(e) => setForm({ ...form, warning_high: e.target.value })} className={inputCls()} /></Field>
            <Field label="Crit Low"><input type="number" step="any" value={form.critical_low ?? ""} onChange={(e) => setForm({ ...form, critical_low: e.target.value })} className={inputCls()} /></Field>
            <Field label="Crit High"><input type="number" step="any" value={form.critical_high ?? ""} onChange={(e) => setForm({ ...form, critical_high: e.target.value })} className={inputCls()} /></Field>
          </div>
          <div className="border-t border-[#2C3238] mt-3 pt-3">
            <div className="scada-label mb-2">PROTOCOL ADDRESSING (for server-side connector)</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Modbus Register"><input type="number" value={form.register_address ?? ""} onChange={(e) => setForm({ ...form, register_address: e.target.value })} placeholder="40001 → 0" className={inputCls()} /></Field>
              <Field label="Reg Type">
                <select value={form.register_type} onChange={(e) => setForm({ ...form, register_type: e.target.value })} className={inputCls()}>
                  <option value="holding">holding</option><option value="input">input</option>
                  <option value="coil">coil</option><option value="discrete">discrete</option>
                </select>
              </Field>
              <Field label="Reg Count"><input type="number" value={form.register_count || 1} onChange={(e) => setForm({ ...form, register_count: e.target.value })} className={inputCls()} /></Field>
            </div>
            <Field label="OPC-UA Node ID"><input value={form.opcua_node_id || ""} onChange={(e) => setForm({ ...form, opcua_node_id: e.target.value })} placeholder="ns=2;s=Channel1.Device1.Tag1" className={inputCls()} /></Field>
          </div>
          <div className="flex justify-end gap-2 mt-4"><PrimaryBtn type="submit" data-testid="save-tag">Save</PrimaryBtn></div>
        </form>
      </Modal>
    </div>
  );
}
